import { useState, useEffect, useCallback } from 'react';
import { isValidTimezone } from '@/lib/timezone';
import { useSearch } from 'wouter';

/**
 * Validates coordinate values
 */
export const isValidCoordinate = (lat: number, lon: number): boolean => {
  return (
    !isNaN(lat) &&
    !isNaN(lon) &&
    lat >= -90 &&
    lat <= 90 &&
    lon >= -180 &&
    lon <= 180
  );
};

/**
 * Parses URL search parameters for location data
 */
export const parseLocationFromURL = (searchString: string): ForecastLocation | null => {
  const params = new URLSearchParams(searchString);
  const latStr = params.get("lat");
  const lonStr = params.get("lon");
  const name = params.get("name");
  const timezoneParam = params.get("timezone");

  if (!latStr || !lonStr || !name) {
    return null;
  }

  const lat = parseFloat(latStr);
  const lon = parseFloat(lonStr);

  if (!isValidCoordinate(lat, lon)) {
    // Invalid coordinates in URL (warn removed)
    return null;
  }

  const timezone = timezoneParam && isValidTimezone(timezoneParam) ? timezoneParam : undefined;

  return { lat, lon, name: decodeLegacyLocationName(name), timezone };
};

function decodeLegacyLocationName(name: string): string {
  try {
    return name.includes('%') ? decodeURIComponent(name) : name;
  } catch {
    return name;
  }
}

/**
 * Updates URL with location parameters without full page navigation
 */
export const updateURLWithLocation = (
  lat: number,
  lon: number,
  name: string,
  timezone?: string
) => {
  const url = new URL(window.location.href);
  url.searchParams.set("lat", lat.toString());
  url.searchParams.set("lon", lon.toString());
  url.searchParams.set("name", name);
  if (timezone) {
    url.searchParams.set("timezone", timezone);
  } else {
    url.searchParams.delete("timezone");
  }
  window.history.pushState({}, "", url.toString());
};

/**
 * Clears location parameters from URL
 */
export const clearLocationFromURL = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete("lat");
  url.searchParams.delete("lon");
  url.searchParams.delete("name");
  url.searchParams.delete("timezone");
  window.history.pushState({}, "", url.toString());
};

export interface ForecastLocation {
  lat: number;
  lon: number;
  name: string;
  timezone?: string;
}

interface UseForecastLocationReturn {
  selectedLocation: ForecastLocation | null;
  setSelectedLocation: (loc: ForecastLocation | null) => void;
  clearLocation: () => void;
}

/**
 * Custom hook for managing forecast location from URL and state
 */
export function useForecastLocation(): UseForecastLocationReturn {
  const searchString = useSearch();
  const [selectedLocation, setSelectedLocationState] = useState<ForecastLocation | null>(null);

  // Parse location from URL on mount / search change
  useEffect(() => {
    const parsed = parseLocationFromURL(searchString);
    if (parsed) {
      setSelectedLocationState(parsed);
    }
  }, [searchString]);

  const setSelectedLocation = useCallback((loc: ForecastLocation | null) => {
    setSelectedLocationState(loc);
    if (loc) {
      updateURLWithLocation(loc.lat, loc.lon, loc.name, loc.timezone);
    } else {
      clearLocationFromURL();
    }
  }, []);

  const clearLocation = useCallback(() => {
    setSelectedLocationState(null);
    clearLocationFromURL();
  }, []);

  return {
    selectedLocation,
    setSelectedLocation,
    clearLocation,
  };
}
